import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Box, Typography, Dialog, IconButton } from '@mui/material';
import WordCloudLib from 'react-d3-cloud';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';

const STOP = new Set([
  'och','att','det','som','en','ett','är','i','på','av','för','med','den','de',
  'till','har','vi','om','men','så','kan','inte','du','han','hon','sin','sig',
  'vara','var','från','när','än','vad','hur','alla','mer','eller','också','man',
  'nu','ska','skulle','upp','ner','där','här','ja','nej','vi','er','dem','dom',
  'this','that','the','and','for','with','are','have','has','was','not','but',
]);

function hash(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (Math.imul(31, h) + str.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function tokenize(text) {
  return text
    .toLowerCase()
    .replace(/[^a-zåäö0-9\s]/g, ' ')
    .split(/[\s,]+/)
    .filter(w => w.length > 2 && !STOP.has(w));
}

// ── Färgscheman ────────────────────────────────────────────────────────────────
const SCHEMES = {
  brand: {
    label: 'Brand',
    fill: (h, rel) => {
      if (rel > 0.6)  return ['#1a1a1a', '#2d2d2d', '#111'][h % 3];
      if (rel > 0.35) return ['#E05500', '#c44300', '#ff6b00'][h % 3];
      if (rel > 0.15) return ['#8B2000', '#b83a00'][h % 2];
      return ['#555', '#666', '#777'][h % 3];
    },
    bg: '#fff',
  },
  mono: {
    label: 'Mono',
    fill: (h, rel) => {
      const light = Math.round(20 + (1 - rel) * 60);
      return `hsl(0,0%,${light}%)`;
    },
    bg: '#fff',
  },
  vibrant: {
    label: 'Vibranta',
    fill: (h) => {
      const hues = [200, 160, 280, 30, 340, 60, 190];
      return `hsl(${hues[h % hues.length]},80%,${35 + (h % 3) * 10}%)`;
    },
    bg: '#fff',
  },
  dark: {
    label: 'Mörkt',
    fill: (h, rel) => {
      if (rel > 0.6)  return '#ffffff';
      if (rel > 0.35) return ['#E05500', '#ff6b00'][h % 2];
      if (rel > 0.15) return '#aaa';
      return '#555';
    },
    bg: 'transparent',
  },
  pastel: {
    label: 'Pastell',
    fill: (h) => {
      const hues = [200, 140, 260, 20, 320, 50, 180];
      return `hsl(${hues[h % hues.length]},60%,65%)`;
    },
    bg: '#fff',
  },
};

// ── Rotationsstilar ────────────────────────────────────────────────────────────
const ROTATIONS = {
  horizontal: { label: 'Horis.', fn: () => 0 },
  mixed:      { label: 'Mixat',  fn: (word) => hash(word.text) % 3 === 0 ? -90 : 0 },
  vertical:   { label: 'Vert.',  fn: () => -90 },
};

// ── Layout-referensstorlek ─────────────────────────────────────────────────────
const REF_W = 720;
const REF_H = 460;

// ── Konfig-toolbar ─────────────────────────────────────────────────────────────
function CloudSettings({ scheme, setScheme, rotation, setRotation }) {
  const btnSx = (active) => ({
    fontSize: '0.68rem', px: 1, py: 0.3, textTransform: 'none', minWidth: 0,
    color: active ? '#fff' : 'text.secondary',
    bgcolor: active ? '#E05500' : 'transparent',
    border: '1px solid',
    borderColor: active ? '#E05500' : 'divider',
    '&:hover': { bgcolor: active ? '#c44300' : 'action.hover' },
    lineHeight: 1.4,
  });

  return (
    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 1, alignItems: 'center' }}>
      <Typography fontSize="0.68rem" color="text.disabled" fontWeight={700} letterSpacing={0.5}>
        FÄRG
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        {Object.entries(SCHEMES).map(([key, s]) => (
          <Box key={key} component="button" onClick={() => setScheme(key)} sx={{ ...btnSx(scheme === key), borderRadius: 1, cursor: 'pointer' }}>
            {s.label}
          </Box>
        ))}
      </Box>
      <Typography fontSize="0.68rem" color="text.disabled" fontWeight={700} letterSpacing={0.5} ml={0.5}>
        ROTATION
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        {Object.entries(ROTATIONS).map(([key, r]) => (
          <Box key={key} component="button" onClick={() => setRotation(key)} sx={{ ...btnSx(rotation === key), borderRadius: 1, cursor: 'pointer' }}>
            {r.label}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ── Hjälp: bygg animerade ord (återanvänds av float + rain) ───────────────────
function AnimWords({ words, scheme, keyframeName, dirUp }) {
  if (!words.length) return null;
  const maxVal = words[0].value;
  const schemeObj = SCHEMES[scheme] || SCHEMES.brand;
  const start = dirUp ? '110vh' : '-110vh';
  const end   = dirUp ? '-110vh' : '110vh';

  return (
    <>
      <style>{`@keyframes ${keyframeName}{0%{opacity:0;transform:translateY(${start})}8%{opacity:var(--wc-op,0.9)}92%{opacity:var(--wc-op,0.9)}100%{opacity:0;transform:translateY(${end})}}`}</style>
      {words.slice(0, 55).map((word) => {
        const h   = hash(word.text);
        const rel = word.value / maxVal;
        const sz  = Math.round(11 + rel * 50);
        const dur = 18 + (h % 28);
        const del = -((h * 97) % (dur * 10)) / 10;
        const lft = ((h * 37 + word.text.length * 11) % 82) + 2;
        const op  = Math.round((0.5 + rel * 0.5) * 100) / 100;
        return (
          <span key={word.text} style={{
            position: 'absolute',
            left: `${lft}%`,
            top: 0,
            fontSize: `${sz}px`,
            color: schemeObj.fill(h, rel),
            fontWeight: rel > 0.6 ? 900 : rel > 0.3 ? 700 : 500,
            fontFamily: '"Inter","Helvetica Neue",Arial,sans-serif',
            whiteSpace: 'nowrap',
            lineHeight: 1,
            '--wc-op': op,
            animationName: keyframeName,
            animationDuration: `${dur}s`,
            animationDelay: `${del}s`,
            animationTimingFunction: 'linear',
            animationIterationCount: 'infinite',
            userSelect: 'none',
          }}>
            {word.text}
          </span>
        );
      })}
    </>
  );
}

// ── Flytande animerat moln (uppåt) ─────────────────────────────────────────────
function FloatingCloud({ words, scheme }) {
  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden' }}>
      <AnimWords words={words} scheme={scheme} keyframeName="wc-float" dirUp />
    </Box>
  );
}

// ── Ordregn (nedåt, tätare, snabbare) ─────────────────────────────────────────
function RainCloud({ words, scheme }) {
  if (!words.length) return null;
  const maxVal = words[0].value;
  const schemeObj = SCHEMES[scheme] || SCHEMES.brand;

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden' }}>
      <style>{`@keyframes wc-rain{0%{opacity:0;transform:translateY(-110vh)}6%{opacity:var(--wc-op,0.9)}94%{opacity:var(--wc-op,0.9)}100%{opacity:0;transform:translateY(110vh)}}`}</style>
      {/* Dubbla uppsättningen för täthet */}
      {[0, 1].map(pass => words.slice(0, 40).map((word) => {
        const h   = hash(word.text + pass);
        const rel = word.value / maxVal;
        const sz  = Math.round(9 + rel * 44);
        const dur = 10 + (h % 16);                           // halv hastighet: 10–25s
        const del = -((h * 73 + pass * 1337) % (dur * 10)) / 10;
        const lft = ((h * 41 + pass * 53 + word.text.length * 7) % 88) + 1;
        const op  = Math.round((0.55 + rel * 0.45) * 100) / 100;
        return (
          <span key={`${pass}-${word.text}`} style={{
            position: 'absolute',
            left: `${lft}%`,
            top: 0,
            fontSize: `${sz}px`,
            color: schemeObj.fill(h, rel),
            fontWeight: rel > 0.6 ? 900 : rel > 0.3 ? 700 : 500,
            fontFamily: '"Inter","Helvetica Neue",Arial,sans-serif',
            whiteSpace: 'nowrap',
            lineHeight: 1,
            '--wc-op': op,
            animationName: 'wc-rain',
            animationDuration: `${dur}s`,
            animationDelay: `${del}s`,
            animationTimingFunction: 'linear',
            animationIterationCount: 'infinite',
            userSelect: 'none',
          }}>
            {word.text}
          </span>
        );
      }))}
    </Box>
  );
}

// ── Statiskt moln — fast layout @ REF_W×REF_H, skalad med CSS ─────────────────
function StaticCloud({ words, scheme, rotation, containerW, containerH }) {
  if (!words.length || containerW === 0 || containerH === 0) return null;
  const maxVal     = words[0].value;
  const schemeObj  = SCHEMES[scheme]    || SCHEMES.brand;
  const rotObj     = ROTATIONS[rotation] || ROTATIONS.mixed;

  const scale = Math.min(containerW / REF_W, containerH / REF_H);
  const ox = Math.max(0, (containerW - REF_W * scale) / 2);
  const oy = Math.max(0, (containerH - REF_H * scale) / 2);

  return (
    <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
      <Box sx={{
        position: 'absolute',
        left: ox,
        top: oy,
        width: REF_W,
        height: REF_H,
        transform: `scale(${scale})`,
        transformOrigin: 'top left',
      }}>
        <WordCloudLib
          key="wc-static"
          data={words}
          width={REF_W}
          height={REF_H}
          font='"Inter","Helvetica Neue",Arial,sans-serif'
          fontWeight={(word) => {
            const rel = word.value / maxVal;
            return rel > 0.6 ? 900 : rel > 0.3 ? 700 : 500;
          }}
          fontSize={(word) => Math.round(13 + (word.value / maxVal) * 59)}
          rotate={rotObj.fn}
          padding={5}
          spiral="archimedean"
          fill={(word) => schemeObj.fill(hash(word.text), word.value / maxVal)}
          random={() => 0.5}
        />
      </Box>
    </Box>
  );
}

// ── Fullskärmsinnehåll med egen storleksmätning ────────────────────────────────
function FullscreenContent({ words, scheme, rotation, mode, showSettings, setScheme, setRotation, onClose }) {
  const ref = useRef(null);
  const [w, setW] = useState(window.innerWidth);
  const [h, setH] = useState(window.innerHeight);

  useEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver(entries => {
      const { width, height } = entries[0]?.contentRect || {};
      if (width  > 0) setW(Math.floor(width));
      if (height > 0) setH(Math.floor(height));
    });
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);

  return (
    <Box ref={ref} sx={{ position: 'relative', width: '100%', height: '100%' }}>
      {mode === 'float' ? <FloatingCloud words={words} scheme={scheme} />
       : mode === 'rain' ? <RainCloud words={words} scheme={scheme} />
       : <StaticCloud words={words} scheme={scheme} rotation={rotation} containerW={w} containerH={h} />
      }
      <IconButton
        onClick={onClose}
        size="small"
        sx={{
          position: 'absolute', top: 14, right: 14, zIndex: 10,
          bgcolor: 'rgba(0,0,0,0.5)', color: '#fff',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
        }}
      >
        <FullscreenExitIcon />
      </IconButton>
      {showSettings && (
        <Box sx={{
          position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)',
          bgcolor: 'rgba(0,0,0,0.72)', borderRadius: 2, px: 2, py: 1,
          backdropFilter: 'blur(8px)',
        }}>
          <CloudSettings scheme={scheme} setScheme={setScheme} rotation={rotation} setRotation={setRotation} />
        </Box>
      )}
    </Box>
  );
}

// ── Komponent ──────────────────────────────────────────────────────────────────
export default function WordCloud({ responses, questionIndex, label, showSettings = false, initialScheme = 'brand' }) {
  const containerRef = useRef(null);
  const [containerW,  setContainerW]  = useState(700);
  const [containerH,  setContainerH]  = useState(400);
  const [scheme,      setScheme]      = useState(initialScheme);
  const [rotation,    setRotation]    = useState('mixed');
  const [mode,        setMode]        = useState('static'); // 'static' | 'float' | 'rain'
  const [fullscreen,  setFullscreen]  = useState(false);

  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(entries => {
      const { width: w, height: h } = entries[0]?.contentRect || {};
      if (w > 0) setContainerW(Math.floor(w));
      if (h > 0) setContainerH(Math.floor(h));
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  const words = useMemo(() => {
    const freq = {};
    for (const r of responses) {
      const ans = r.answers?.[questionIndex];
      if (!ans) continue;
      for (const word of tokenize(String(ans))) {
        freq[word] = (freq[word] || 0) + 1;
      }
    }
    return Object.entries(freq)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 80)
      .map(([text, value]) => ({ text: text.toUpperCase(), value }));
  }, [responses, questionIndex]);

  if (words.length === 0) return (
    <Box sx={{ p: 2, color: 'text.secondary', fontSize: '0.82rem', fontStyle: 'italic' }}>
      Inga svar att visa ännu.
    </Box>
  );

  const schemeObj = SCHEMES[scheme] || SCHEMES.brand;

  const modeBtnSx = (active) => ({
    fontSize: '0.62rem', px: 0.9, py: 0.25,
    lineHeight: 1.4, minWidth: 0, cursor: 'pointer',
    bgcolor: active ? 'rgba(224,85,0,0.85)' : 'rgba(0,0,0,0.38)',
    color: active ? '#fff' : 'rgba(255,255,255,0.6)',
    border: `1px solid ${active ? '#E05500' : 'rgba(255,255,255,0.15)'}`,
    borderRadius: '4px',
    '&:hover': { bgcolor: active ? '#E05500' : 'rgba(0,0,0,0.6)', color: '#fff' },
  });

  const renderCloud = (cw, ch) => {
    if (mode === 'float') return <FloatingCloud words={words} scheme={scheme} />;
    if (mode === 'rain')  return <RainCloud     words={words} scheme={scheme} />;
    return <StaticCloud words={words} scheme={scheme} rotation={rotation} containerW={cw} containerH={ch} />;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {label && (
        <Typography color="text.secondary" fontSize="0.75rem" mb={1}>{label}</Typography>
      )}
      {showSettings && (
        <CloudSettings
          scheme={scheme} setScheme={setScheme}
          rotation={rotation} setRotation={setRotation}
        />
      )}
      <Box
        ref={containerRef}
        sx={{
          position: 'relative',
          flex: 1,
          minHeight: 0,
          bgcolor: schemeObj.bg,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        {renderCloud(containerW, containerH)}

        {/* Lägesväljare + fullskärm */}
        <Box sx={{ position: 'absolute', top: 5, right: 5, display: 'flex', gap: 0.5, zIndex: 5 }}>
          {[['static','Still'],['float','Flyt'],['rain','Regn']].map(([m, label]) => (
            <Box key={m} component="button" onClick={() => setMode(m)} sx={modeBtnSx(mode === m)}>
              {label}
            </Box>
          ))}
          <IconButton size="small" onClick={() => setFullscreen(true)} title="Helskärm"
            sx={{ width: 24, height: 24, bgcolor: 'rgba(0,0,0,0.38)', color: 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '4px', '&:hover': { bgcolor: 'rgba(0,0,0,0.65)', color: '#fff' } }}
          >
            <FullscreenIcon sx={{ fontSize: 13 }} />
          </IconButton>
        </Box>
      </Box>

      {/* Fullskärmsläge */}
      <Dialog
        open={fullscreen}
        onClose={() => setFullscreen(false)}
        fullScreen
        PaperProps={{ sx: { bgcolor: schemeObj.bg || '#111', overflow: 'hidden' } }}
      >
        <FullscreenContent
          words={words}
          scheme={scheme}
          rotation={rotation}
          mode={mode}
          showSettings={showSettings}
          setScheme={setScheme}
          setRotation={setRotation}
          onClose={() => setFullscreen(false)}
        />
      </Dialog>
    </Box>
  );
}
